import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { notification } from "antd";
import {
  IoAlbumsOutline,
  IoChevronForwardOutline,
  IoEyeOutline,
  IoPeopleOutline,
  IoPersonCircleOutline,
  IoPlayCircleOutline,
  IoRadioButtonOnOutline,
  IoRefreshOutline,
  IoSparklesOutline,
  IoStatsChartOutline,
  IoTimeOutline,
  IoTrophyOutline,
  IoTvOutline,
} from "react-icons/io5";
import {
  getLocaleForLanguage,
  localizeTournamentName,
  normalizeLanguage,
} from "../../services/language";
import { buildPlayerHistoryRoute } from "./leaderboardProfileNavigation";

const LEARN_HERO_IMAGE = "/learn/assets/generated/xima-learn-hero-20260615.png";

const SPECTATE_COPY = Object.freeze({
  lobby: { es: "Lobby", en: "Lobby", de: "Lobby", ru: "Лобби" },
  watchMatches: { es: "Ver Partidos", en: "Watch Matches", de: "Partien ansehen", ru: "Смотреть партии" },
  heroDescription: {
    es: "Partidas en vivo, incluyendo jugadores contra bots. Entra como espectador.",
    en: "Live matches, including players versus bots. Join as a spectator.",
    de: "Live-Partien, auch Spieler gegen Bots. Tritt als Zuschauer bei.",
    ru: "Партии в прямом эфире, включая игры с ботами. Войди как зритель.",
  },
  refresh: { es: "Actualizar", en: "Refresh", de: "Aktualisieren", ru: "Обновить" },
  refreshing: { es: "Actualizando...", en: "Refreshing...", de: "Aktualisieren...", ru: "Обновление..." },
  live: { es: "En vivo", en: "Live", de: "Live", ru: "В эфире" },
  connectedPlayers: {
    es: "Jugadores en partida",
    en: "Players in match",
    de: "Spieler in der Partie",
    ru: "Игроки в партии",
  },
  spectators: { es: "Espectadores", en: "Spectators", de: "Zuschauer", ru: "Зрители" },
  replays: { es: "Replays", en: "Replays", de: "Wiederholungen", ru: "Повторы" },
  onlineRooms: { es: "Salas online", en: "Online rooms", de: "Online-Räume", ru: "Онлайн-комнаты" },
  liveMatches: { es: "Partidos en vivo", en: "Live matches", de: "Live-Partien", ru: "Партии в прямом эфире" },
  noLiveMatches: {
    es: "No hay partidas activas ahora mismo.",
    en: "There are no active matches right now.",
    de: "Derzeit gibt es keine aktiven Partien.",
    ru: "Сейчас нет активных партий.",
  },
  bestHistoricalMatches: {
    es: "Mejores partidas históricas",
    en: "Best historic matches",
    de: "Beste historische Partien",
    ru: "Лучшие исторические партии",
  },
  featuredReplays: {
    es: "Repeticiones destacadas",
    en: "Featured replays",
    de: "Empfohlene Wiederholungen",
    ru: "Рекомендуемые повторы",
  },
  bestHistoricalSubtitle: {
    es: "Si no hay partidas en vivo, aquí siempre queda una buena partida para aprender.",
    en: "If there are no live matches, there is always a good match here to learn from.",
    de: "Wenn es keine Live-Partien gibt, findest du hier immer eine gute Partie zum Lernen.",
    ru: "Если сейчас нет партий в прямом эфире, здесь всегда есть хорошая партия для обучения.",
  },
  bestHumanMatchByLevel: {
    es: "Mejor partida humana por nivel",
    en: "Best human match by level",
    de: "Beste menschliche Partie nach Stufe",
    ru: "Лучшая партия человека по уровню",
  },
  featuredHumanMatch: {
    es: "Partida humana destacada",
    en: "Featured human match",
    de: "Empfohlene menschliche Partie",
    ru: "Рекомендуемая партия человека",
  },
  humanVsBotMatch: {
    es: "Partida humano vs bot",
    en: "Human vs bot match",
    de: "Mensch gegen Bot Partie",
    ru: "Партия человек против бота",
  },
  featuredMatch: { es: "Partida destacada", en: "Featured match", de: "Empfohlene Partie", ru: "Рекомендуемая партия" },
  challengeCreatedBy: {
    es: "Desafío creado por {name}",
    en: "Challenge created by {name}",
    de: "Herausforderung erstellt von {name}",
    ru: "Вызов создан {name}",
  },
  cimaxModeScore: {
    es: "CIMAX {mode}: {score}",
    en: "CIMAX {mode}: {score}",
    de: "CIMAX {mode}: {score}",
    ru: "CIMAX {mode}: {score}",
  },
  twoPlayerGameWithDiceChoice: {
    es: "Partida de 2 jugadores: elige moverte con o sin dado. 10 minutos.",
    en: "Two-player game: choose to move with or without dice. 10 minutes.",
    de: "Partie für 2 Spieler: Wähle, ob du mit oder ohne Würfel ziehen möchtest. 10 Minuten.",
    ru: "Партия для 2 игроков: выбери, делать ходы с кубиком или без него. 10 минут.",
  },
  onlineMatch: { es: "Partida Online", en: "Online Match", de: "Online-Partie", ru: "Онлайн-партия" },
  historicalMatch: { es: "Partida histórica", en: "Historical Match", de: "Historische Partie", ru: "Историческая партия" },
  mode: { es: "Modo", en: "Mode", de: "Modus", ru: "Режим" },
  time: { es: "Tiempo", en: "Time", de: "Zeit", ru: "Время" },
  players: { es: "Jugadores", en: "Players", de: "Spieler", ru: "Игроки" },
  level: { es: "Nivel", en: "Level", de: "Stufe", ru: "Уровень" },
  pending: { es: "por registrar", en: "pending", de: "ausstehend", ru: "ожидается" },
  replay: { es: "Repetición", en: "Replay", de: "Wiederholung", ru: "Повтор" },
  watchReplay: { es: "Ver repetición", en: "Watch replay", de: "Wiederholung ansehen", ru: "Смотреть повтор" },
  watchLive: { es: "Ver en vivo", en: "Watch live", de: "Live ansehen", ru: "Смотреть в эфире" },
  joining: { es: "Entrando...", en: "Joining...", de: "Beitreten...", ru: "Вход..." },
  noPlayerData: {
    es: "Sin datos de jugadores",
    en: "No player data",
    de: "Keine Spielerdaten",
    ru: "Нет данных об игроках",
  },
  errorTitle: { es: "Error", en: "Error", de: "Fehler", ru: "Ошибка" },
  liveMatchesLoadError: {
    es: "No se pudieron cargar los partidos en vivo.",
    en: "Could not load live matches.",
    de: "Live-Partien konnten nicht geladen werden.",
    ru: "Не удалось загрузить партии в прямом эфире.",
  },
  botSuffix: { es: "BOT", en: "BOT", de: "BOT", ru: "BOT" },
  science: { es: "Ciencia", en: "Science", de: "Wissenschaft", ru: "Наука" },
  chance: { es: "Azar", en: "Chance", de: "Zufall", ru: "Случайность" },
  cienzar: { es: "CIENZAR", en: "CIENZAR", de: "CIENZAR", ru: "CIENZAR" },
  scienceVsAzar: {
    es: "Contrazar",
    en: "Contrazar",
    de: "Contrazar",
    ru: "Contrazar",
  },
});

const getSpectateCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = SPECTATE_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const formatSpectateNumber = (value, language) =>
  new Intl.NumberFormat(getLocaleForLanguage(language), {
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(Number(value) || 0)));

const normalizeDisplayModeKey = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalized === "free" || normalized === "botlocal" || normalized === "botlocalfree") {
    return "science";
  }
  if (normalized === "dice" || normalized === "botlocaldice") return "chance";
  if (
    [
      "sciencevsazar",
      "science-vs-azar",
      "science vs azar",
      "science v/s azar",
      "science vs chance",
      "science v/s chance",
      "cienciavsazar",
      "ciencia-vs-azar",
      "ciencia vs azar",
      "ciencia v/s azar",
      "contrazar",
      "localsciencevsazar",
      "botlocalsciencevsazar",
    ].includes(normalized)
  ) {
    return "scienceVsAzar";
  }

  if (["ciencia", "science", "wissenschaft", "наука"].includes(normalized)) {
    return "science";
  }
  if (["azar", "chance", "zufall", "случайность", "random", "free"].includes(normalized)) {
    return "chance";
  }
  if (["cienzar", "dice", "botlocalcienzar"].includes(normalized)) {
    return "cienzar";
  }
  if (
    [
      "sciencevsazar",
      "science-vs-azar",
      "cienciavsazar",
      "ciencia-vs-azar",
      "localsciencevsazar",
      "botlocalsciencevsazar",
    ].includes(normalized)
  ) {
    return "scienceVsAzar";
  }
  return "";
};

const getModeCopyKey = (mode) => {
  const normalized = String(mode || "").trim().toLowerCase();
  if (normalized === "free" || normalized === "botlocal" || normalized === "botlocalfree") {
    return "science";
  }
  if (normalized === "dice" || normalized === "botlocaldice") return "chance";
  if (normalized === "cienzar" || normalized === "botlocalcienzar") return "cienzar";
  if (
    normalized === "sciencevsazar" ||
    normalized === "science-vs-azar" ||
    normalized === "science vs azar" ||
    normalized === "science v/s azar" ||
    normalized === "science vs chance" ||
    normalized === "science v/s chance" ||
    normalized === "cienciavsazar" ||
    normalized === "ciencia-vs-azar" ||
    normalized === "ciencia vs azar" ||
    normalized === "ciencia v/s azar" ||
    normalized === "contrazar" ||
    normalized === "localsciencevsazar" ||
    normalized === "botlocalsciencevsazar"
  ) {
    return "scienceVsAzar";
  }
  return normalizeDisplayModeKey(mode);
};

const formatMode = (mode, language = "es") => {
  const copyKey = getModeCopyKey(mode);
  if (copyKey) return getSpectateCopy(language, copyKey);
  return String(mode || "").trim().toUpperCase();
};

const localizeModeLabel = (value, language = "es") => {
  const copyKey = normalizeDisplayModeKey(value) || getModeCopyKey(value);
  if (copyKey) return getSpectateCopy(language, copyKey);
  return String(value || "").trim().toUpperCase();
};

const formatHistoricalKindLabel = (kind, language = "es") => {
  const normalizedKind = String(kind || "").trim();
  if (normalizedKind === "best_human_duel") {
    return getSpectateCopy(language, "bestHumanMatchByLevel");
  }
  if (normalizedKind === "human_duel") {
    return getSpectateCopy(language, "featuredHumanMatch");
  }
  if (normalizedKind === "human_bot_duel") {
    return getSpectateCopy(language, "humanVsBotMatch");
  }
  return getSpectateCopy(language, "featuredMatch");
};

const localizeHistoricalTitle = (language, value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const challengeMatch = /^(?:desaf[ií]o creado por|challenge created by)\s+(.+?)(?:\s+\(CIMAX\s+([^:()]+):\s*([^)]+)\))?$/i.exec(raw);
  if (challengeMatch) {
    const playerName = String(challengeMatch[1] || "").trim();
    const mode = String(challengeMatch[2] || "").trim();
    const score = String(challengeMatch[3] || "").trim();
    const challengeText = getSpectateCopy(language, "challengeCreatedBy", { name: playerName });
    if (!mode || !score) return challengeText;
    const cimaxText = getSpectateCopy(language, "cimaxModeScore", {
      mode: localizeModeLabel(mode, language),
      score,
    });
    return `${challengeText} (${cimaxText})`;
  }

  const normalized = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
  if (
    normalized.includes("2-player game") ||
    normalized.includes("partida de 2 jugadores") ||
    normalized.includes("partida para 2 jugadores") ||
    normalized.includes("juego entre 2 jugadores")
  ) {
    return getSpectateCopy(language, "twoPlayerGameWithDiceChoice");
  }

  return localizeTournamentName(language, raw) || raw;
};

const normalizePlayerCode = (value) => {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (/^\d{4}$/.test(raw)) return `XIMA-${raw}`;
  return raw;
};

const resolveLivePlayerProfileTarget = (player) => {
  if (player && typeof player === "object") {
    const byCode = normalizePlayerCode(player?.playerCode);
    if (byCode) return byCode;
    const byLogin = String(player?.loginIdentifier || player?.email || "").trim();
    if (byLogin) return byLogin;
    const byId = String(player?.userId || player?._id || player?.id || "").trim();
    if (byId) return byId;
    return String(player?.displayName || player?.name || "").trim();
  }
  return String(player || "").trim();
};

const resolveLivePlayerLabel = (player) => {
  if (player && typeof player === "object") {
    return String(
      player?.displayName ||
        player?.name ||
        player?.playerCode ||
        player?.email ||
        player?.userId ||
        player?._id ||
        "-"
    ).trim();
  }
  return String(player || "").trim() || "-";
};

const formatHistoricalDate = (value, language = "es") => {
  const date = new Date(value || 0);
  if (!Number.isFinite(date.valueOf()) || date.valueOf() <= 0) return "";
  const formattedDate = new Intl.DateTimeFormat(getLocaleForLanguage(language), {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);

  if (normalizeLanguage(language) === "ru") {
    return formattedDate.replace(/\s?г\.$/i, "");
  }

  return formattedDate;
};

const getSpectateModeTone = (mode) => {
  const normalized = String(mode || "").toLowerCase();
  if (
    normalized === "sciencevsazar" ||
    normalized === "localsciencevsazar" ||
    normalized === "botlocalsciencevsazar"
  ) {
    return "sciencevsazar";
  }
  if (["free", "botlocal", "botlocalfree"].includes(normalized)) return "ciencia";
  if (["dice", "botlocaldice"].includes(normalized)) return "azar";
  if (["cienzar", "botlocalcienzar"].includes(normalized)) return "cienzar";
  return "neutral";
};

const SpectateMatches = () => {
  const navigate = useNavigate();
  const { language: outletLanguage = "es" } = useOutletContext() || {};
  const language = normalizeLanguage(outletLanguage);
  const ui = useCallback(
    (key, values) => getSpectateCopy(language, key, values),
    [language]
  );
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [joiningRoomId, setJoiningRoomId] = useState("");

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/tournaments/live");
      setMatches(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      notification.error({
        message: ui("errorTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          ui("liveMatchesLoadError"),
      });
    } finally {
      setLoading(false);
    }
  }, [ui]);

  useEffect(() => {
    loadMatches();
    const intervalId = window.setInterval(loadMatches, 5000);
    return () => window.clearInterval(intervalId);
  }, [loadMatches]);

  const liveMatches = useMemo(
    () => matches.filter((match) => String(match?.type || "live").toLowerCase() !== "historical"),
    [matches]
  );
  const historicalMatches = useMemo(
    () => matches.filter((match) => String(match?.type || "").toLowerCase() === "historical"),
    [matches]
  );
  const totalSpectators = useMemo(
    () =>
      liveMatches.reduce((sum, match) => sum + Math.max(0, Number(match?.spectators) || 0), 0),
    [liveMatches]
  );
  const totalConnectedPlayers = useMemo(
    () =>
      liveMatches.reduce(
        (sum, match) => sum + (Array.isArray(match?.players) ? match.players.length : 0),
        0
      ),
    [liveMatches]
  );

  const handleWatchMatch = useCallback(
    (match) => {
      const tournamentId = String(match?.tournamentId || "");
      const roomId = String(match?.roomId || "");
      if (!tournamentId || !roomId) return;

      setJoiningRoomId(roomId);
      navigate(
        `/game/${tournamentId}?autoplay=1&spectator=1&roomId=${encodeURIComponent(roomId)}`
      );
    },
    [navigate]
  );
  const handleWatchHistoricalMatch = useCallback(
    (match) => {
      const historyId = String(match?.historyId || match?.id || "").trim();
      if (!historyId || match?.hasReplay === false) return;
      navigate("/game/history", {
        state: {
          openReplayRequestKey: `watch-highlight:${historyId}:${Date.now()}`,
          openReplayMatch: {
            id: historyId,
            historyId,
            tournamentName: match?.tournamentName || "Partida historica",
            mode: match?.mode || "",
            modeLabel: match?.modeLabel || formatMode(match?.mode, language),
            hasReplay: true,
            replaySteps: Number(match?.replaySteps) || 0,
            participants: Array.isArray(match?.players) ? match.players : [],
          },
        },
      });
    },
    [language, navigate]
  );
  const handleOpenPlayerProfile = useCallback(
    (player) => {
      const target = resolveLivePlayerProfileTarget(player);
      if (!target) return;
      navigate(buildPlayerHistoryRoute(target));
    },
    [navigate]
  );

  return (
    <main className="xima-spectate-screen">
      <div className="xima-spectate-shell">
        <section className="xima-spectate-hero">
          <div className="xima-spectate-hero-copy">
            <span className="xima-spectate-title-icon">
              <IoTvOutline aria-hidden="true" />
            </span>
            <div>
              <span className="xima-spectate-kicker">{ui("lobby")}</span>
              <h1>{ui("watchMatches")}</h1>
              <p>{ui("heroDescription")}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={loadMatches}
            disabled={loading}
            className="xima-spectate-refresh-button"
            style={{ zIndex: 3 }}
          >
            <IoRefreshOutline aria-hidden="true" />
            <span>{loading ? ui("refreshing") : ui("refresh")}</span>
          </button>

          <div
            className="xima-learn-hero-image-wrap xima-spectate-hero-art"
            aria-hidden="true"
            style={{ zIndex: 1 }}
          >
            <img src={LEARN_HERO_IMAGE} alt="" aria-hidden="true" />
          </div>
        </section>

        <section className="xima-spectate-stats">
          <article>
            <IoRadioButtonOnOutline aria-hidden="true" />
            <span>{formatSpectateNumber(liveMatches.length, language)}</span>
            <small>{ui("live")}</small>
          </article>
          <article>
            <IoPeopleOutline aria-hidden="true" />
            <span>{formatSpectateNumber(totalConnectedPlayers, language)}</span>
            <small>{ui("connectedPlayers")}</small>
          </article>
          <article>
            <IoEyeOutline aria-hidden="true" />
            <span>{formatSpectateNumber(totalSpectators, language)}</span>
            <small>{ui("spectators")}</small>
          </article>
          <article>
            <IoAlbumsOutline aria-hidden="true" />
            <span>{formatSpectateNumber(historicalMatches.length, language)}</span>
            <small>{ui("replays")}</small>
          </article>
        </section>

        <section className="xima-spectate-panel">
          <div className="xima-spectate-section-head">
            <span className="xima-spectate-section-icon xima-spectate-live-icon">
              <IoPlayCircleOutline aria-hidden="true" />
            </span>
            <div>
              <small>{ui("onlineRooms")}</small>
              <h2>{ui("liveMatches")}</h2>
            </div>
          </div>

          {liveMatches.length === 0 ? (
            <div className="xima-spectate-empty">
              <IoEyeOutline aria-hidden="true" />
              <span>{ui("noLiveMatches")}</span>
            </div>
          ) : (
            <div className="xima-spectate-match-grid">
              {liveMatches.map((match) => {
                const roomId = String(match?.roomId || "");
                const tournamentId = String(match?.tournamentId || "");
                const players = Array.isArray(match?.players) ? match.players : [];
                const hasBots = players.some((player) => Boolean(player?.isBot));
                const spectators = Number(match?.spectators) || 0;
                const memberSize = Number(match?.memberSize) || 2;
                const turnTime = Number(match?.havingTime) || 1;
                const tone = getSpectateModeTone(match?.mode);

                return (
                  <article
                    key={`${tournamentId}-${roomId}`}
                    className={`xima-spectate-match-card xima-spectate-match-${tone}`}
                  >
                    <div className="xima-spectate-card-main">
                      <div>
                        <span className="xima-spectate-live-badge">
                          <IoRadioButtonOnOutline aria-hidden="true" />
                          {ui("live")}
                        </span>
                        <h3>
                          {localizeHistoricalTitle(language, match?.tournamentName) ||
                            (hasBots ? ui("humanVsBotMatch") : ui("onlineMatch"))}
                        </h3>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleWatchMatch(match)}
                        disabled={joiningRoomId === roomId}
                        className="xima-spectate-watch-button"
                      >
                        <IoEyeOutline aria-hidden="true" />
                        <span>
                          {joiningRoomId === roomId
                            ? ui("joining")
                            : ui("watchLive")}
                        </span>
                        <IoChevronForwardOutline aria-hidden="true" />
                      </button>
                    </div>

                    <div className="xima-spectate-tags">
                      <span>
                        <IoSparklesOutline aria-hidden="true" />
                        {ui("mode")}: {formatMode(match?.mode, language)}
                      </span>
                      <span>
                        <IoTimeOutline aria-hidden="true" />
                        {ui("time")}: {String(turnTime).padStart(2, "0")}:00
                      </span>
                      <span>
                        <IoPeopleOutline aria-hidden="true" />
                        {ui("players")}: {formatSpectateNumber(players.length, language)}/{formatSpectateNumber(memberSize, language)}
                      </span>
                      <span>
                        <IoEyeOutline aria-hidden="true" />
                        {ui("spectators")}: {formatSpectateNumber(spectators, language)}
                      </span>
                    </div>

                    <div className="xima-spectate-players">
                      <div className="xima-spectate-subtitle">
                        <IoPersonCircleOutline aria-hidden="true" />
                        <span>{ui("connectedPlayers")}</span>
                      </div>
                      <div className="xima-spectate-player-list">
                        {players.length > 0 ? (
                          players.map((player, playerIndex) => (
                            <button
                              type="button"
                              key={`${roomId}-${resolveLivePlayerLabel(player)}-${playerIndex}`}
                              onClick={() => {
                                if (!player?.isBot) handleOpenPlayerProfile(player);
                              }}
                              disabled={Boolean(player?.isBot)}
                              className="xima-spectate-player-chip"
                            >
                              {resolveLivePlayerLabel(player)}
                              {player?.isBot ? ` ${ui("botSuffix")}` : ""}
                            </button>
                          ))
                        ) : (
                          <span className="xima-spectate-muted">
                            {ui("noPlayerData")}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {historicalMatches.length > 0 ? (
          <section className="xima-spectate-panel xima-spectate-history-panel">
            <div className="xima-spectate-section-head">
              <span className="xima-spectate-section-icon xima-spectate-history-icon">
                <IoTrophyOutline aria-hidden="true" />
              </span>
              <div>
                <small>{ui("bestHistoricalMatches")}</small>
                <h2>{ui("featuredReplays")}</h2>
                <p>{ui("bestHistoricalSubtitle")}</p>
              </div>
            </div>

            <div className="xima-spectate-match-grid">
              {historicalMatches.map((match) => {
                const historyId = String(match?.historyId || match?.id || "");
                const players = Array.isArray(match?.players) ? match.players : [];
                const levelGame = Number(match?.levelGame) || 0;
                const replaySteps = Number(match?.replaySteps) || 0;
                const endedAtLabel = formatHistoricalDate(match?.endedAt, language);
                const kindLabel = formatHistoricalKindLabel(match?.historicalKind, language);
                const tone = getSpectateModeTone(match?.mode);

                return (
                  <article
                    key={`history-${historyId}`}
                    className={`xima-spectate-match-card xima-spectate-history-card xima-spectate-match-${tone}`}
                  >
                    <div className="xima-spectate-card-main">
                      <div>
                        <span className="xima-spectate-history-badge">
                          <IoAlbumsOutline aria-hidden="true" />
                          {kindLabel}
                        </span>
                        <h3>
                          {localizeHistoricalTitle(language, match?.tournamentName) ||
                            ui("historicalMatch")}
                        </h3>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleWatchHistoricalMatch(match)}
                        disabled={!match?.hasReplay}
                        className="xima-spectate-replay-button"
                      >
                        <IoPlayCircleOutline aria-hidden="true" />
                        <span>{ui("watchReplay")}</span>
                        <IoChevronForwardOutline aria-hidden="true" />
                      </button>
                    </div>

                    <div className="xima-spectate-tags">
                      <span>
                        <IoSparklesOutline aria-hidden="true" />
                        {ui("mode")}: {localizeModeLabel(match?.modeLabel || match?.mode, language)}
                      </span>
                      <span>
                        <IoStatsChartOutline aria-hidden="true" />
                        {ui("level")}:{" "}
                        {levelGame > 0 ? formatSpectateNumber(levelGame, language) : ui("pending")}
                      </span>
                      <span>
                        <IoAlbumsOutline aria-hidden="true" />
                        {ui("replay")}: {formatSpectateNumber(replaySteps, language)}
                      </span>
                      {endedAtLabel ? (
                        <span>
                          <IoTimeOutline aria-hidden="true" />
                          {endedAtLabel}
                        </span>
                      ) : null}
                    </div>

                    <div className="xima-spectate-players">
                      <div className="xima-spectate-subtitle">
                        <IoPeopleOutline aria-hidden="true" />
                        <span>{ui("players")}</span>
                      </div>
                      <div className="xima-spectate-player-list">
                        {players.length > 0 ? (
                          players.map((player, playerIndex) => (
                            <button
                              type="button"
                              key={`${historyId}-${resolveLivePlayerLabel(player)}-${playerIndex}`}
                              onClick={() => handleOpenPlayerProfile(player)}
                              className="xima-spectate-player-chip"
                            >
                              {resolveLivePlayerLabel(player)}
                              {player?.isBot ? ` ${ui("botSuffix")}` : ""}
                            </button>
                          ))
                        ) : (
                          <span className="xima-spectate-muted">
                            {ui("noPlayerData")}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}

      </div>
    </main>
  );
};

export default SpectateMatches;
